import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import bookingsApi from "@/api/bookingsApi";
import propertiesApi from "@/api/propertiesApi";
import BookingCalendar from "@/components/BookingCalendar/BookingCalendar";
import ApiError from "@/model/ApiError";
import DateRange from "@/model/DateRange";
import PropertyResponse from "@/model/PropertyResponse";
import isoDate from "@/utils/isoDate";
import "@/pages/CreateBookingPage/CreateBookingPage.scss";

/**
 * Lets a host (or admin) book nights on a property for themselves — typically to block dates
 * for owner stays, maintenance or bookings taken outside the platform.
 */
function CreateBookingPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [property, setProperty] = useState<PropertyResponse | null>(null);
  const [bookedRanges, setBookedRanges] = useState<DateRange[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [adults, setAdults] = useState("1");
  const [children, setChildren] = useState("0");
  const [infants, setInfants] = useState("0");

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!session || !id) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    Promise.all([
      propertiesApi.getPropertyById(session.accessToken, id),
      propertiesApi.getAvailability(id),
    ])
      .then(([loadedProperty, availability]) => {
        if (cancelled) {
          return;
        }
        setProperty(loadedProperty);
        setBookedRanges(
          (availability.bookedPeriods ?? [])
            .filter((period) => period.checkIn && period.checkOut)
            .map((period) => ({
              start: isoDate.parseIsoDate(period.checkIn as string),
              end: isoDate.parseIsoDate(period.checkOut as string),
            }))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  if (!session || !id) {
    return null;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!checkIn || !checkOut) {
      setError(t("bookings.create.selectDatesError"));
      return;
    }

    setIsSubmitting(true);
    try {
      await bookingsApi.createBooking(session.accessToken, {
        propertyId: id,
        checkIn: isoDate.toIsoDate(checkIn),
        checkOut: isoDate.toIsoDate(checkOut),
        guests: {
          adults: Number(adults),
          children: Number(children) || 0,
          infants: Number(infants) || 0,
        },
        // Hosts block their own dates: confirmed straight away, never charged.
        ownerBlock: true,
      });
      navigate(`/properties/${id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t("bookings.create.conflictError"));
      } else if (err instanceof ApiError && err.status === 400) {
        setError(err.message || t("bookings.create.validationError"));
      } else {
        setError(t("bookings.create.genericError"));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-booking-page">
      <form className="create-booking-page__form" onSubmit={handleSubmit}>
        <h1>{t("bookings.create.title")}</h1>
        <p className="create-booking-page__help">{t("bookings.create.help")}</p>

        {isLoading && <p>{t("bookings.create.loading")}</p>}
        {loadError && <p role="alert">{t("bookings.create.loadError")}</p>}

        {!isLoading && !loadError && property && (
          <>
            <p className="create-booking-page__property">{property.title}</p>

            <BookingCalendar
              bookedRanges={bookedRanges}
              checkIn={checkIn}
              checkOut={checkOut}
              minNights={property.minNights ?? 1}
              onChange={(nextCheckIn, nextCheckOut) => {
                setCheckIn(nextCheckIn);
                setCheckOut(nextCheckOut);
              }}
            />

            <p className="create-booking-page__summary">
              {checkIn && checkOut
                ? t("bookings.create.summary", {
                    checkIn: isoDate.formatIsoDate(isoDate.toIsoDate(checkIn)),
                    checkOut: isoDate.formatIsoDate(isoDate.toIsoDate(checkOut)),
                  })
                : t("bookings.create.noDates")}
            </p>

            <fieldset className="create-booking-page__guests">
              <legend>{t("bookings.create.guestsLabel")}</legend>
              <div>
                <label htmlFor="adults">{t("guests.adults")}</label>
                <input
                  id="adults"
                  type="number"
                  min={1}
                  max={50}
                  value={adults}
                  onChange={(event) => setAdults(event.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="children">{t("guests.children")}</label>
                <input
                  id="children"
                  type="number"
                  min={0}
                  max={50}
                  value={children}
                  onChange={(event) => setChildren(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="infants">{t("guests.infants")}</label>
                <input
                  id="infants"
                  type="number"
                  min={0}
                  max={5}
                  value={infants}
                  onChange={(event) => setInfants(event.target.value)}
                />
              </div>
            </fieldset>
          </>
        )}

        {error && <p className="create-booking-page__error">{error}</p>}

        <div className="create-booking-page__actions">
          <button type="submit" disabled={isSubmitting || isLoading || loadError}>
            {isSubmitting ? t("bookings.create.submitting") : t("bookings.create.submit")}
          </button>
          <Link to={`/properties/${id}`}>{t("bookings.create.cancel")}</Link>
        </div>
      </form>
    </div>
  );
}

export default CreateBookingPage;
